import { IStreamingProvider } from '@honeybook/core';
import { boundMethod } from 'autobind-decorator';
import { v4 as uuidv4 } from 'uuid';
import {
    TransportAlreadyStartedError,
    InvalidResponseError,
    IWebsocketService
} from '@honeybook/core-web';

type WebsocketStreamingProviderOptions = {
    webSocketsService: IWebsocketService;
    roomId: string;
    userId: string;
};

export class WebWebsocketStreamingProvider<T> implements IStreamingProvider<T> {
    private websocketCallbackId: string;
    private awaitingResponsesBuffer: T[] = [];
    private signalOnNewResponses: (() => void) | null = null;
    protected readonly webSocketsService: IWebsocketService;
    protected readonly roomId: string;
    protected readonly userId: string;
    private leaveRoomCB: () => void;

    constructor({
        webSocketsService,
        roomId,
        userId
    }: WebsocketStreamingProviderOptions) {
        this.webSocketsService = webSocketsService;
        this.roomId = roomId;
        this.userId = userId;
    }

    @boundMethod
    onResponse(data: T) {
        if (!data) {
            throw new InvalidResponseError();
        }

        this.awaitingResponsesBuffer = [...this.awaitingResponsesBuffer, data];

        // Signal on the next responses
        if (this.signalOnNewResponses) {
            this.signalOnNewResponses();
            this.signalOnNewResponses = null;
        }
    }

    async start() {
        if (this.websocketCallbackId) {
            throw new TransportAlreadyStartedError();
        }

        this.websocketCallbackId = uuidv4();
        try {
            const namespace = {
                userId: this.userId
            };

            const namespaceToken =
                await this.webSocketsService.getNamespaceToken(
                    namespace,
                    'streaming'
                );
            this.leaveRoomCB = this.webSocketsService.joinRoom({
                callback: this.onResponse,
                callbackId: this.websocketCallbackId,
                roomName: this.roomId,
                namespace,
                namespaceToken
            });
        } catch (e) {
            this.clean();
        }
    }

    async stop() {
        if (!this.websocketCallbackId) {
            return;
        }

        if (this.leaveRoomCB) {
            this.leaveRoomCB();
        }

        this.clean();
    }

    private clean() {
        this.websocketCallbackId = null;
        this.leaveRoomCB = null;
    }

    async *stream(): AsyncGenerator<T, void, unknown> {
        while (true) {
            if (this.awaitingResponsesBuffer.length === 0) {
                // Nothing ready yet - wait for next push
                await new Promise<void>(resolve => {
                    this.signalOnNewResponses = resolve;
                });
                continue;
            }

            yield this.awaitingResponsesBuffer.shift();
        }
    }
}
